import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Activity, ArrowDownCircle, ArrowUpCircle, Plus, Download, RefreshCw, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { API, AuthContext } from '../App';
import { useT } from '../contexts/LanguageContext';
import { useCurrency, formatCurrency } from '../contexts/CompanyContext';
import { generateReportPDF } from '../lib/pdf';

const TYPE_META = {
  receipt:    { icon: ArrowDownCircle, color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Receipt (IN)' },
  issue:      { icon: ArrowUpCircle,   color: 'text-red-600',     bg: 'bg-red-100',     label: 'Issue (OUT)' },
  adjustment: { icon: RefreshCw,       color: 'text-amber-600',   bg: 'bg-amber-100',   label: 'Adjustment' },
  return:     { icon: ArrowDownCircle, color: 'text-blue-600',    bg: 'bg-blue-100',    label: 'Return (IN)' },
};

const CAN_MANUAL = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT'];

export default function StockMovements() {
  const t = useT();
  const { user } = useContext(AuthContext);
  const { symbol, company } = useCurrency();
  const [movements, setMovements] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [itemFilter, setItemFilter] = useState('all');
  const [showNew, setShowNew] = useState(false);

  const [draft, setDraft] = useState({
    stock_item_id: '', movement_type: 'receipt', quantity: 0, unit_price: 0,
    supplier_id: '', notes: '', reason: ''
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [m, s] = await Promise.all([
        axios.get(`${API}/stock-movements`, { withCredentials: true }),
        axios.get(`${API}/stock-items`, { withCredentials: true })
      ]);
      setMovements(m.data);
      setStockItems(s.data);
    } catch { toast.error('Failed to load movements'); }
    setLoading(false);
  };

  const createMovement = async () => {
    if (!draft.stock_item_id || !draft.quantity) {
      toast.error('Stock item and quantity are required'); return;
    }
    try {
      const payload = {
        ...draft,
        quantity: parseFloat(draft.quantity),
        unit_price: parseFloat(draft.unit_price) || 0
      };
      await axios.post(`${API}/stock-movements`, payload, { withCredentials: true });
      toast.success('Movement recorded · stock updated');
      setShowNew(false);
      setDraft({ stock_item_id: '', movement_type: 'receipt', quantity: 0, unit_price: 0, supplier_id: '', notes: '', reason: '' });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const filtered = movements.filter(m => {
    if (filter !== 'all' && m.movement_type !== filter) return false;
    if (itemFilter !== 'all' && m.stock_item_id !== itemFilter) return false;
    return true;
  });

  const totalIn = filtered.filter(m => m.quantity > 0).reduce((s, m) => s + (m.total_value || 0), 0);
  const totalOut = filtered.filter(m => m.quantity < 0).reduce((s, m) => s + (m.total_value || 0), 0);

  const downloadAuditPDF = () => {
    const rows = filtered.map(m => [
      new Date(m.created_at).toLocaleString(),
      m.item_name || '-',
      TYPE_META[m.movement_type]?.label || m.movement_type,
      `${m.quantity > 0 ? '+' : ''}${m.quantity}`,
      `${symbol} ${(m.unit_price || 0).toFixed(2)}`,
      `${symbol} ${(m.total_value || 0).toFixed(2)}`,
      String(m.balance_after ?? '-'),
      m.performed_by_name || '-',
      m.complaint_id ? `Complaint: ${m.complaint_id.slice(0, 8)}` :
        m.service_report_id ? `SR: ${m.service_report_id.slice(0, 8)}` :
        m.reason || '-'
    ]);
    generateReportPDF({
      title: 'Stock Movement Audit Trail',
      subtitle: `${filtered.length} movement(s) — full reconciliation record as of ${new Date().toLocaleDateString()}`,
      company,
      sections: [
        {
          heading: 'Summary',
          head: ['Metric', 'Value'],
          rows: [
            ['Total movements', String(filtered.length)],
            ['Total IN (value)', `${symbol} ${totalIn.toFixed(2)}`],
            ['Total OUT (value)', `${symbol} ${totalOut.toFixed(2)}`],
            ['Net change (value)', `${symbol} ${(totalIn - totalOut).toFixed(2)}`]
          ]
        },
        {
          heading: 'Movements',
          head: ['Timestamp', 'Item', 'Type', 'Qty', 'Unit Cost', 'Value', 'Balance', 'Performed By', 'Link/Reason'],
          rows
        }
      ],
      filename: `stock_audit_${new Date().toISOString().split('T')[0]}.pdf`
    });
    toast.success('Audit PDF generated');
  };

  return (
    <Layout>
      <div data-testid="stock-movements-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <Activity className="w-6 h-6 text-teal-600" />{t('mv.title')}
            </h1>
            <p className="text-sm text-muted-foreground">{t('mv.subtitle')}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="download-audit-pdf" onClick={downloadAuditPDF} variant="outline" disabled={!filtered.length}>
              <Download className="w-4 h-4 mr-1" />Audit PDF
            </Button>
            {CAN_MANUAL.includes(user?.role) && (
              <Dialog open={showNew} onOpenChange={setShowNew}>
                <DialogTrigger asChild>
                  <Button data-testid="new-movement-btn" className="bg-teal-600 hover:bg-teal-700">
                    <Plus className="w-4 h-4 mr-1" />Manual Movement
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle>Record Manual Stock Movement</DialogTitle></DialogHeader>
                  <div className="space-y-3 mt-2">
                    <div>
                      <Label>Stock Item *</Label>
                      <Select value={draft.stock_item_id} onValueChange={(v) => setDraft({ ...draft, stock_item_id: v })}>
                        <SelectTrigger data-testid="mv-item-select"><SelectValue placeholder="Select item" /></SelectTrigger>
                        <SelectContent>
                          {stockItems.map(s => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.item_name} ({s.current_quantity} {s.unit})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Type *</Label>
                        <Select value={draft.movement_type} onValueChange={(v) => setDraft({ ...draft, movement_type: v })}>
                          <SelectTrigger data-testid="mv-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="receipt">Receipt (stock IN)</SelectItem>
                            <SelectItem value="issue">Issue (stock OUT)</SelectItem>
                            <SelectItem value="adjustment">Adjustment (+/-)</SelectItem>
                            <SelectItem value="return">Return (stock IN)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Quantity *</Label>
                        <Input type="number" data-testid="mv-qty-input" value={draft.quantity} onChange={(e) => setDraft({ ...draft, quantity: e.target.value })} placeholder={draft.movement_type === 'adjustment' ? '+ or -' : 'positive'} />
                      </div>
                    </div>
                    <div>
                      <Label>Unit Cost ({symbol})</Label>
                      <Input type="number" value={draft.unit_price} onChange={(e) => setDraft({ ...draft, unit_price: e.target.value })} placeholder="Leave 0 to use current stock price" />
                    </div>
                    <div>
                      <Label>Reason</Label>
                      <Input value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} placeholder="e.g. Purchase from Supplier X, cycle count correction" />
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
                    </div>
                    <Button data-testid="submit-movement" onClick={createMovement} className="w-full bg-teal-600 hover:bg-teal-700">Record Movement</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="p-4">
            <div className="text-xs text-muted-foreground">Movements</div>
            <div className="text-2xl font-bold text-foreground">{filtered.length}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs text-muted-foreground">Total IN (value)</div>
            <div className="text-2xl font-bold text-emerald-600">{formatCurrency(totalIn, symbol)}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs text-muted-foreground">Total OUT (value)</div>
            <div className="text-2xl font-bold text-red-600">{formatCurrency(totalOut, symbol)}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs text-muted-foreground">Net change</div>
            <div className={`text-2xl font-bold ${totalIn - totalOut >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {formatCurrency(totalIn - totalOut, symbol)}
            </div>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          <Filter className="w-4 h-4 text-slate-500" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="mv-filter-type" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="receipt">Receipts</SelectItem>
              <SelectItem value="issue">Issues</SelectItem>
              <SelectItem value="adjustment">Adjustments</SelectItem>
              <SelectItem value="return">Returns</SelectItem>
            </SelectContent>
          </Select>
          <Select value={itemFilter} onValueChange={setItemFilter}>
            <SelectTrigger data-testid="mv-filter-item" className="w-56"><SelectValue placeholder="All items" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All items</SelectItem>
              {stockItems.map(s => <SelectItem key={s.id} value={s.id}>{s.item_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Movements list */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-muted-foreground">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Activity className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 font-medium">No movements yet</p>
              <p className="text-sm text-muted-foreground mt-1">Movements are auto-logged on stock receipt + material issues linked to service reports.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs text-slate-600 uppercase tracking-wide">
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3">Item</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3 text-right">Qty</th>
                    <th className="px-4 py-3 text-right hidden md:table-cell">Unit</th>
                    <th className="px-4 py-3 text-right hidden md:table-cell">Value</th>
                    <th className="px-4 py-3 text-right hidden lg:table-cell">Balance</th>
                    <th className="px-4 py-3 hidden sm:table-cell">By</th>
                    <th className="px-4 py-3">Link / Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map(m => {
                    const meta = TYPE_META[m.movement_type] || {};
                    const Icon = meta.icon || Activity;
                    return (
                      <tr key={m.id} data-testid={`movement-${m.id}`} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">
                          {new Date(m.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-slate-900">{m.item_name || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${meta.bg} ${meta.color}`}>
                            <Icon className="w-3 h-3" />{meta.label || m.movement_type}
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-right text-sm font-bold ${m.quantity > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {m.quantity > 0 ? '+' : ''}{m.quantity}
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-slate-600 hidden md:table-cell">
                          {formatCurrency(m.unit_price || 0, symbol)}
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-slate-700 hidden md:table-cell">
                          {formatCurrency(m.total_value || 0, symbol)}
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-slate-700 hidden lg:table-cell font-semibold">
                          {m.balance_after ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600 hidden sm:table-cell">{m.performed_by_name || '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-600">
                          {m.complaint_id && <span className="text-teal-700">Complaint #{m.complaint_id.slice(0, 8)}</span>}
                          {!m.complaint_id && m.service_report_id && <span className="text-teal-700">SR #{m.service_report_id.slice(0, 8)}</span>}
                          {!m.complaint_id && !m.service_report_id && (m.reason || m.notes || '—')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
